import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Box } from '@mui/material';

interface MarkdownRendererProps {
  content: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const htmlContent = useMemo(() => {
    // Configure marked options
    marked.setOptions({
      gfm: true,
      breaks: true,
    });

    const rawHtml = marked(content);
    // Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(rawHtml as string, {
      ADD_ATTR: ['target'], // Allow target attribute for links
    });
  }, [content]);

  return (
    <Box
      sx={{
        '& h1': {
          fontSize: '2rem',
          fontWeight: 700,
          mt: 4,
          mb: 2,
          color: 'text.primary',
        },
        '& h2': {
          fontSize: '1.5rem',
          fontWeight: 600,
          mt: 4,
          mb: 2,
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1,
        },
        '& h3': {
          fontSize: '1.25rem',
          fontWeight: 600,
          mt: 3,
          mb: 1.5,
          color: 'text.primary',
        },
        '& h4, & h5, & h6': {
          fontWeight: 600,
          mt: 2,
          mb: 1,
          color: 'text.primary',
        },
        '& p': {
          fontSize: '1rem',
          lineHeight: 1.7,
          mb: 2,
          color: 'text.secondary',
        },
        '& ul, & ol': {
          pl: 3,
          mb: 2,
          '& li': {
            mb: 0.5,
            color: 'text.secondary',
            lineHeight: 1.7,
          },
        },
        '& a': {
          color: 'primary.main',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        '& blockquote': {
          borderLeft: 4,
          borderColor: 'primary.main',
          pl: 2,
          py: 1,
          my: 2,
          bgcolor: 'grey.50',
          '& p': {
            mb: 0,
            fontStyle: 'italic',
          },
        },
        '& code': {
          bgcolor: 'grey.100',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          fontFamily: 'monospace',
          fontSize: '0.875em',
        },
        '& pre': {
          bgcolor: 'grey.900',
          color: 'grey.100',
          p: 2,
          borderRadius: 1,
          overflow: 'auto',
          my: 2,
          '& code': {
            bgcolor: 'transparent',
            p: 0,
            color: 'inherit',
          },
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1,
          my: 2,
        },
        '& hr': {
          border: 'none',
          borderTop: 1,
          borderColor: 'divider',
          my: 4,
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          my: 2,
          '& th, & td': {
            border: 1,
            borderColor: 'divider',
            p: 1.5,
            textAlign: 'left',
          },
          '& th': {
            bgcolor: 'grey.50',
            fontWeight: 600,
          },
        },
      }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default MarkdownRenderer;
